/**
 * Функция фильтрует и обогащает массив объектов (arr) данными из объекта calc по ключу id каждого элемента.
 *
 * Цель: Создать новый массив с атрибутами из calc (name, price, position и т.д.), но сохранить оригинальное
 * количество (piece) из arr. Не мутировать оригинальный arr и его элементы (иммутабельность).
 *
 * Параметры:
 * - arr: Массив объектов вида [{ piece: number, id: string }, ...] — оригинальные данные (количества и ID).
 * - pos: Строка (опционально) — позиция для фильтрации (если задана, фильтрует по el.position === pos;
 *   иначе фильтрует, исключая position === "table").
 *
 * Возврат: Новый массив объектов с объединёнными данными, где каждый элемент имеет:
 * - piece из arr (сохранён),
 * - свойства из calc[el.id] (кроме piece, чтобы не перезаписывать),
 * - active: true (добавляется всегда).
 *
 * Важные особенности:
 * - Использует иммутабельные операции (spread, map, filter, forEach) — оригинал arr не меняется.
 * - Исключает piece из calc, чтобы сохранить оригинальное количество из arr (избежать перезаписи на 0).
 * - Если calc[el.id] не существует, элемент остаётся с базовыми свойствами из el + active: true.
 *
 * Пример использования:
 * const filtered = filterArrayFromItem(servObj.building._objects);
 * // Результат: Массив с сохранённым piece, но обогащённый name, price и т.д. из calc.
 */
export const filterArrayFromItem = (arr, pos = "") => {
    const servObj = window.servObj || parent.servObj;
    const calc = servObj.calc;

    // Шаг 1: Создаём поверхностную копию массива arr.
    // Почему? Чтобы избежать мутации оригинального arr (map и filter работают на копии).
    // [...arr] создаёт новый массив с ссылками на те же объекты, но поскольку мы создадим новые объекты в map,
    // оригинал останется нетронутым.
    const copyArray = [...arr];

    // Шаг 2: Обогащаем каждый элемент данными из calc, создавая новые объекты (map).
    // map возвращает новый массив; внутри колбека создаём новый объект res для каждого el,
    // чтобы не мутировать оригинальные объекты из arr.
    let resultArray = copyArray.map((el) => {
        // Создаём новый объект res как копию el с помощью spread-оператора ({ ...el }).
        // Почему? Это сохраняет все свойства el (включая piece и id) в новом объекте.
        // Spread создаёт поверхностную копию — достаточно, если el простой объект без глубоких вложенностей.
        // Если el содержит сложные вложенные объекты, рассмотрите structuredClone(el) для глубокой копии.
        let res = { ...el }; // Spread для копии el (сохраняет piece)

        // Получаем данные из calc по ключу el.id (например, calc["box_carpet"]).
        // calc — глобальный объект с атрибутами (name, price, position и т.д.) для каждого id.
        // Если calcData undefined (нет данных для id), то ничего не добавляем.
        const calcData = calc[el.id];

        // Если calcData существует, копируем его свойства в res, но исключаем "piece".
        // Почему исключать piece? В calc piece часто дефолтное (0 или 1), и мы не хотим
        // перезаписывать оригинальное количество из arr (el.piece). Это предотвращает потерю данных.
        // Используем forEach для итерации по ключам calcData — это позволяет выборочно копировать свойства.
        // Альтернатива: Object.assign(res, { ...calcData, piece: undefined }), но forEach более явный.
        if (calcData) {
            Object.keys(calcData).forEach((key) => {
                if (key !== "piece") {
                    // Пропускаем piece из calc — сохраняем оригинальный el.piece в res.
                    // Это ключевой шаг для фикса бага: piece теперь берётся только из arr.
                    res[key] = calcData[key];
                }
            });
        }

        // Добавляем свойство active: true на новый объект res.
        // Почему? Это флаг активности, требуемый для дальнейшей логики (например, calcActives).
        // Поскольку res — новый объект, это не влияет на оригинал.
        res.active = true;

        // Возвращаем новый объект res — map соберёт их в resultArray.
        // Важно: res — независимый от el, так что оригинальные объекты в arr не мутированы.
        return res;
    });

    // Шаг 3: Фильтруем resultArray по позиции (position).
    // filter возвращает новый массив, не мутируя resultArray.
    // Почему? Чтобы отобрать релевантные элементы (например, исключить "table" для дефолтного случая).
    // - Если pos задан (не пустая строка): фильтруем по точному совпадению el.position === pos.
    // - Иначе: исключаем элементы с position === "table" (вероятно, для отображения только "down", "base" и т.д.).
    // Если фильтр не нужен, resultArray будет содержать все элементы после map.
    if (pos.length) {
        // Фильтр по конкретной позиции (например, pos = "down" → только элементы с position: "down").
        // Это полезно для выборки подмножества (например, только для определённой секции).
        resultArray = resultArray.filter((el) => el.position === pos);
    } else {
        // Дефолтный фильтр: исключаем "table" (элементы на столе/вне модели?).
        // position берётся из calcData, так что фильтр работает на обогащённых данных.
        // Пример: В вашем случае исключает "tumba__ts_10" (если его position = "table").
        // resultArray = resultArray.filter((el) => el.position !== "table");
    }

    // Возвращаем финальный новый массив resultArray.
    // Он содержит: сохранённый piece, обогащённые атрибуты из calc, active: true, и отфильтрованные элементы.
    // Оригинальный arr остаётся неизменным — функция чистая (pure function).
    return resultArray;
};
